<template>
  <div id="main-content">
    <div class="content-page-banner">
      <div class="banner-content">
        <h1>{{ $route.meta.title }}</h1>
        <div class="underline"></div>
      </div>
    </div>
    <RecordsView
      :apiStatus="apiStatus"
      :referenceData="referenceData"
      :recordsData="recordsData"
      :includeFilter="true"
      :includeRecordHeader="false"
      :filterData="filterData"
      :recordLink="recordLink"
      @updatedFilterData="updateFilterData"
      @crmNavigatePage="crmNavigatePage"
    >
      <!-- Show Claim now section only if there's an extras product -->
      <template v-if="currentIsExtrasFlag || isCRM" v-slot:callOutColumn>
        <div class="call-out call-to-action box">
          <div class="content-container">
            <figure>
              <img :src="referenceData.callOutIconUrl" alt="callout icon" />
            </figure>
            <div class="text-container" v-if="!isCRM && referenceData.callOutText && !checkSomeObjectPropertiesTrue(apiStatus.refData)" v-html="referenceData.callOutText" />
            <template v-if="!isClaim">
              <router-link v-if="!isCRM" :to="{ name: BENEFIT_QUOTE_GET_A_BENEFIT, query: { step: 1 } }" class="button crimson">{{ callOutButtonText }}</router-link>
              <button v-else @click="crmNavigatePage(CRM_PAGES.GABQ)" class="button crimson">{{ callOutButtonText }}</button>
            </template>
            <template v-else>
              <router-link v-if="!isCRM" :to="{ name: CLAIMS_MAKE_A_CLAIM, query: { step: 1 } }" class="button crimson">{{ callOutButtonText }}</router-link>
              <button v-else @click="crmNavigatePage(CRM_PAGES.GABQ)" class="button crimson">{{ callOutButtonText }}</button>
            </template>
          </div>
        </div>
      </template>
      <template v-slot:pagination>
        <Pagination isSPA @toPageNumber="paginationNavigation" :endPage="lastPageNumber" :currentPageFromParent="pageNum" />
      </template>
    </RecordsView>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { VUEX_BENEFIT_QUOTES_CLAIMS, VUEX_GENERAL } from '@/constants/VuexConstants';
import BenefitQuotesServices from '@/services/BenefitQuotes';
import ClaimsServices from '@/services/Claims';
import RecordsView from '@/components/RecordsView.vue';
import { setQueryString } from '@/utils/UrlUtils';
import { formatAsCurrency, getImageUrl, isBuildCRM, isDateFormat, checkWithinXYears, convertDateToYYYYMMDD, sortByObjectProperty, getProductCodeFromModality } from '@/utils';
import {
  getCurrentDate,
  getPastOrLaterDateFromInitialDate,
  checkSomeObjectPropertiesTrue,
  reverseFormatFormDate,
  formatFormDate,
  isDateBefore,
  toSentenceCase,
} from '@general/js/utils';
import { BENEFIT_QUOTE_GET_A_BENEFIT, BENEFIT_QUOTE_RESULT, CLAIMS_MAKE_A_CLAIM, CLAIMS_RESULT, ERROR_404_PAGE_NOT_FOUND } from '@/router/routeNames';
import Pagination from '@general/js/components/Pagination.vue';
import { useVuelidate } from '@vuelidate/core';
import { FilterTypes } from '@/enums';
import CRMSupportMixins from './CRMSupportMixins';

const isCRM = isBuildCRM();

export default {
  name: 'BenefitQuotesClaimsHome',

  props: {
    // If true, page would considered it is claims landing page. default to be false
    isClaim: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    RecordsView,
    Pagination,
  },

  mixins: [CRMSupportMixins],

  setup() {
    return { v$: useVuelidate() };
  },

  data() {
    return {
      isCRM,
      BENEFIT_QUOTE_GET_A_BENEFIT,
      CLAIMS_MAKE_A_CLAIM,
      pageNum: 1,
      pageSize: 10,
      totalNum: null,
      quotes: [],
      allQuotes: [],
      recordsData: {
        headings: [],
        data: [],
        noRecordsText: `You do not have any ${this.isClaim ? 'claims' : 'quotes'} to be displayed currently.`,
      },
      members: [],
      claimTypes: [],
      claimStatus: [],
      apiStatus: {
        refData: {
          fail: false,
          loading: true,
        },
        recordData: {
          fail: false,
          loading: true,
        },
      },
      filterData: {
        filterName: this.isClaim ? 'claims' : 'quotes',
        filterFunction: this.applyFilters,
        filterOptions: [],
      },
      key: '',
      filterResult: {
        selectedMember: '',
        selectedClaimType: '',
        enteredID: '',
        selectedClaimStatus: '',
      },
      result: '',
      changeDate: '',
      startDate: '',
      endDate: '',
      searchResultMessage: '',
      lastPageNumber: NaN,
      referenceData: {
        informationText: undefined,
        callOutText: undefined,
        noRecordsText: undefined,
        coverStartDate: undefined,
      },
      isClaimInitialLoad: true,
      recordLink: undefined,
    };
  },

  validations: {
    startDate: {
      isDateFormat(startDate) {
        if (startDate === '') {
          return true;
        }
        return isDateFormat(startDate);
      },
      isWithin2Years(startDate) {
        if (this.isClaim) {
          if (isDateFormat(startDate)) {
            if (this.referenceData.coverStartDate) {
              const twoYearsFromCurrentDate = reverseFormatFormDate(getPastOrLaterDateFromInitialDate(getCurrentDate(), -2));
              if (isDateBefore(reverseFormatFormDate(this.referenceData.coverStartDate), twoYearsFromCurrentDate)) {
                return !isDateBefore(reverseFormatFormDate(this.referenceData.coverStartDate), startDate);
              }
            }
            return checkWithinXYears(convertDateToYYYYMMDD(startDate), getCurrentDate(), 2);
          }
        }
        return true;
      },
    },
    endDate: {
      isDateFormat(endDate) {
        if (endDate === '') {
          return true;
        }
        return isDateFormat(endDate);
      },
    },
  },

  watch: {
    '$route.query.pageNum': {
      handler(newNum, oldNum) {
        if (newNum !== oldNum) {
          this.paginationNavigation(newNum);
        }
      },
    },

    isClaim() {
      this.pageNum = 1;
      this.startDate = '';
      this.endDate = '';
      this.filterResult.selectedMember = '';
      this.filterResult.selectedClaimType = '';
      this.filterResult.enteredID = '';
      this.filterResult.selectedClaimStatus = '';
      this.isClaimInitialLoad = true;
      this.filterData.filterName = this.isClaim ? 'recent claims' : 'recent quotes';
      this.claimTypes = [];
      this.members = [];
      this.claimStatus = [];
      this.updateRecordLink();
      this.getReferenceData();
      this.getFilteredOptions();
      this.getFetchQuotes();

      // Clear the session storage when arriving on this landing page
      this.resetState();
    },
  },

  beforeMount() {
    // if is not CRM and current policy's `isExtras` flag is false, redirect to member portal homepage
    if (!this.isCRM) {
      if (!this.currentIsExtrasFlag && !this.isClaim) {
        this.$router.push({ name: ERROR_404_PAGE_NOT_FOUND });
      } else {
        // always set pageNum url query param to 1
        setQueryString('pageNum', 1);
      }
    }
  },

  mounted() {
    this.updateRecordLink();
    this.getReferenceData();
    this.getFetchQuotes();
    this.getFilteredOptions();

    // Clear the session storage when arriving on this landing page
    this.resetState();
  },

  computed: {
    ...mapGetters({
      policyNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      currentIsExtrasFlag: VUEX_GENERAL.GETTERS.getCurrentPolicyIsExtrasFlag,
    }),

    ...mapGetters('BenefitQuotesClaims', {
      refData: VUEX_BENEFIT_QUOTES_CLAIMS.GETTERS.getRefData,
    }),

    callOutButtonText() {
      return !this.isClaim ? 'Get a benefit quote' : 'Claim now';
    },

    textBenefitQuoteOrClaim() {
      return !this.isClaim ? 'benefit quote' : 'claim';
    },
  },

  methods: {
    checkSomeObjectPropertiesTrue,

    ...mapMutations('BenefitQuotesClaims', {
      resetState: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.resetState,
      updateRefProducts: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.updateRefProducts,
    }),

    async getReferenceData() {
      this.apiStatus.refData.loading = true;
      this.apiStatus.refData.fail = false;

      try {
        if (!this.isCRM) {
          const service = !this.isClaim ? BenefitQuotesServices : ClaimsServices;
          let refData;
          if (!this.isClaim) {
            refData = await service.getLandingPageReferenceData();
          } else {
            refData = await service.getLandingPageReferenceData(this.policyNumber, getCurrentDate());
          }

          this.referenceData = {
            ...refData,
            informationText: !this.isClaim ? refData.benefitQuoteInformationText.informationText : refData.referenceData.claimInformationText.informationText,
            callOutText: !this.isClaim ? refData.benefitQuoteCalloutText.informationText : refData.referenceData.claimCalloutText.informationText,
            callOutIconUrl: !this.isClaim ? getImageUrl(refData.benefitQuoteCalloutIcon.informationText) : getImageUrl(refData.referenceData.claimCalloutIcon.informationText),
          };

          this.recordsData.noRecordsText = `You do not have any ${this.isClaim ? 'claims' : 'quotes'} to be displayed currently.`;
          if (this.isClaim) {
            this.referenceData.coverStartDate = refData.coverStartDate;
          }
          this.getFilteredOptions();
        }
      } catch (error) {
        this.apiStatus.refData.fail = true;
        if (error.message) {
          logger.log(error.message);
        } else {
          logger.log(error);
        }
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },

    // Fetch mock data
    /**
     * FIXME: pageNum, claimNumber, dateFrom, dateTo, member, claimType, claimStatus are added for future live api implimentation. some may not be used and deleted
     */
    async getFetchQuotes(pageNum = this.pageNum, claimNumber, dateFrom, dateTo, member, claimType, claimStatus) {
      this.apiStatus.recordData.loading = true;
      this.apiStatus.recordData.fail = false;

      try {
        const policyNumber = this.isCRM ? this.CRM_POLICY_NUMBER : this.policyNumber;
        const service = !this.isClaim ? BenefitQuotesServices : ClaimsServices;
        const listKey = !this.isClaim ? 'benefitQuoteList' : 'claims';

        let data;
        if (!this.isClaim) {
          data = await Promise.all([
            service.search(policyNumber, pageNum, claimNumber, dateFrom, dateTo, member, claimType, claimStatus),
            service.getMembersProductsAndModalities(policyNumber),
          ]);
        } else {
          data = await Promise.all([service.getClaimsSummary(policyNumber), service.getMembersProductsAndModalities(policyNumber)]);
        }

        const productList = !this.isClaim ? data[1]?.products : data[1]?.claimsMemberModalities?.products;
        const memberList = !this.isClaim ? data[1]?.members : data[1]?.claimsMemberModalities?.members;
        this.updateRefProducts(productList);
        this.allQuotes = data[0][listKey].map((obj) => {
          // eslint-disable-next-line arrow-body-style
          const memberObject = memberList.find(memberItem => memberItem.personCode === (obj.personCodes ? obj.personCodes[0] : obj.personCode));
          if(memberObject){
            // eslint-disable-next-line arrow-body-style
            const memberProducts = this.refData.products?.filter(productItem => memberObject.products.some(memberProduct => memberProduct.productCode === productItem.productCode));
            memberProducts.forEach(memberProduct => {
              // eslint-disable-next-line arrow-body-style
              memberProduct.startDate = memberObject.products.find(productItem => productItem.productCode === memberProduct.productCode).startDate;
              // eslint-disable-next-line arrow-body-style
              memberProduct.endDate = memberObject.products.find(productItem => productItem.productCode === memberProduct.productCode).endDate;
            });
            const quoteObj = {
              ...obj,
              benefitPayable: !this.isClaim ? obj.benefitPayable : obj.benefitsPaid,
              id: obj[!this.isClaim ? 'benefitQuoteId' : 'claimId'].toUpperCase(),
              date: obj[!this.isClaim ? 'quotationDate' : 'submittedDate'],
              productCode: !this.isClaim ? getProductCodeFromModality(obj.modalityName, memberProducts, obj.serviceDate) : getProductCodeFromModality(obj.claimTypes[0], memberProducts, obj.serviceDate),
            };
            quoteObj[!this.isClaim ? 'Quote #' : 'Claim #'] = quoteObj.id;
            quoteObj.Member = !this.isClaim ? quoteObj.memberFullName : `${quoteObj.personNames.join(' & ')}`;
            quoteObj['Claim type'] = !this.isClaim ? quoteObj.modalityName : quoteObj.claimTypes[0];
            quoteObj.Submitted = reverseFormatFormDate(quoteObj.date);
            quoteObj['Quote date'] = reverseFormatFormDate(quoteObj.date);
            quoteObj['Benefits paid'] = quoteObj.benefitPayable !== null ? formatAsCurrency(quoteObj.benefitPayable) : formatAsCurrency(0);
            quoteObj.Status = toSentenceCase(quoteObj.status);
            return quoteObj;
          }
          return null;
        // eslint-disable-next-line arrow-body-style
        }).filter(q => q !== null);
        if (this.isClaim) {
          this.allQuotes = sortByObjectProperty(this.allQuotes, 'date', 'Claim #');
        }

        this.searchResultMessage = this.allQuotes.length < 1 ? 'You do not have any quotes to be displayed currently.' : '';
        this.totalNum = data[0][listKey].length;
        this.quotes = this.allQuotes.slice(0, this.pageSize);
        this.result = this.allQuotes;
        this.recordsData.data = this.quotes;
        if (this.recordsData.data.length > 0) {
          this.recordsData.headings = this.getRecordHeadings();
        } else {
          this.recordsData.headings = [];
        }
        this.lastPageNumber = Math.ceil(this.totalNum / this.pageSize);

        // Add options to filters
        this.allQuotes.forEach((element) => {
          if (!this.members.includes(element.Member) && element.Member !== null) {
            this.members.push(element.Member);
          }

          if (!this.claimTypes.includes(element['Claim type']) && element['Claim type'] !== null) {
            this.claimTypes.push(element['Claim type']);
          }

          if (this.isClaim && !this.claimStatus.includes(element.Status) && element.Status !== null) {
            this.claimStatus.push(element.Status);
          }
        });
      } catch (error) {
        this.apiStatus.recordData.fail = true;
        if (error.message) {
          logger.error(error.message);
        } else {
          logger.error(error);
        }
      } finally {
        // Remove the loading screen
        this.apiStatus.recordData.loading = false;
      }
    },

    // pagination function
    paginationNavigation(pageNum) {
      this.pageNum = Number(pageNum);
      this.$route.query.pageNum = String(pageNum);
      if (this.result !== '') {
        this.quotes = this.result.slice((this.pageNum - 1) * this.pageSize, this.pageNum * this.pageSize);
        this.recordsData.data = this.quotes;
      } else {
        this.quotes = this.allQuotes.slice((this.pageNum - 1) * this.pageSize, this.pageNum * this.pageSize);
        this.recordsData.data = this.quotes;
      }
      this.recordsData.data = this.quotes;
    },

    getRecordHeadings() {
      if (this.isClaim) {
        return [
          'Claim #',
          'Member',
          'Claim type',
          'Submitted',
          'Benefits paid',
          // 'Status' // this is being temporarily disabled for R1
        ];
      }
      return ['Quote #', 'Member', 'Claim type', 'Quote date'];
    },

    getFilteredOptions() {
      this.filterData.filterFunction = this.applyFilters;
      if (this.isClaim) {
        const currentDate = getCurrentDate();
        if (this.referenceData.coverStartDate && checkWithinXYears(this.referenceData.coverStartDate, currentDate, 2)) {
          this.startDate = reverseFormatFormDate(this.referenceData.coverStartDate);
        } else {
          this.startDate = reverseFormatFormDate(getPastOrLaterDateFromInitialDate(currentDate, -2));
        }
      }
      this.filterData.filterOptions = [
        {
          filterHeading: !this.isClaim ? 'Quote #' : 'Claim #',
          filterType: FilterTypes.TEXT_INPUT,
          filterPlaceholder: !this.isClaim ? 'Enter quote #' : 'Claim number',
          filterResult: this.filterResult.enteredID,
        },
        {
          filterHeading: 'Date from',
          filterType: FilterTypes.DATE_INPUT,
          filterPlaceholder: 'DD / MM / YYYY',
          filterResult: this.startDate,
          filterResultInvalid: this.v$.startDate.$invalid,
          filterInvalidMessage: this.dateErrorMessage(this.startDate),
        },
        {
          filterHeading: 'Date to',
          filterType: FilterTypes.DATE_INPUT,
          filterPlaceholder: 'DD / MM / YYYY',
          filterResult: this.endDate,
          filterResultInvalid: this.v$.endDate.$invalid,
          filterInvalidMessage: `Please enter a valid date`,
        },
        {
          filterHeading: 'Member',
          filterType: FilterTypes.DROPDOWN_INPUT,
          filterPlaceholder: 'Select member',
          filterResult: this.filterResult.selectedMember,
          filterDropdownData: this.members,
        },
        {
          filterHeading: 'Claim type',
          filterType: FilterTypes.DROPDOWN_INPUT,
          filterPlaceholder: 'Select claim type',
          filterResult: this.filterResult.selectedClaimType,
          filterDropdownData: this.claimTypes,
        },
      ];
      if (this.isClaim) {
        // this is being temporarily disabled for R1
        // this.filterData.filterOptions.push({
        //   filterHeading: 'Claim status',
        //   filterType: FilterTypes.DROPDOWN_INPUT,
        //   filterPlaceholder: 'All statuses',
        //   filterResult: this.filterResult.selectedClaimStatus,
        //   filterDropdownData: this.claimStatus,
        // });
      }
    },

    updateFilterData(filterData) {
      this.filterData = filterData;
      // const maxFilterOptions = this.isClaim ? 6 : 5; // this is being temporarily disabled for R1
      const maxFilterOptions = 5;
      if (filterData.filterOptions.length === maxFilterOptions) {
        this.filterResult.enteredID = filterData.filterOptions[0].filterResult;

        this.startDate = filterData.filterOptions[1].filterResult;
        filterData.filterOptions[1].filterResultInvalid = this.v$.startDate.$invalid;
        filterData.filterOptions[1].filterInvalidMessage = this.dateErrorMessage(this.startDate);

        this.endDate = filterData.filterOptions[2].filterResult;
        filterData.filterOptions[2].filterResultInvalid = this.v$.endDate.$invalid;

        // Dropdown data update
        const dropdownResults = ['selectedMember', 'selectedClaimType', 'selectedClaimStatus'];
        for (let i = 3; i <= maxFilterOptions - 1; i++) {
          const filterOption = filterData.filterOptions[i];

          if (filterOption.filterResult === filterOption.filterPlaceholder) {
            // resets data if placeholder is reselected in dropdown
            this.filterResult[dropdownResults[i - 3]] = '';
            filterData.filterOptions[i].filterResult = '';
          } else {
            this.filterResult[dropdownResults[i - 3]] = filterData.filterOptions[i].filterResult;
          }
        }
      } else {
        logger.error('Filter options do not have correct length');
      }
    },

    dateErrorMessage(date) {
      if (!isDateFormat(date)) {
        return `Please enter a valid date`;
      }
      if (this.isClaim) {
        const currentDate = getCurrentDate();
        const twoYearsBackFromCurrentDate = reverseFormatFormDate(getPastOrLaterDateFromInitialDate(currentDate, -2));
        if (this.referenceData.coverStartDate) {
          const coverStartDate = reverseFormatFormDate(this.referenceData.coverStartDate);
          if (isDateBefore(coverStartDate, twoYearsBackFromCurrentDate)) {
            if (isDateBefore(coverStartDate, date)) {
              return `Claims history from ${coverStartDate} is available`;
            }
          }
        }
        if (!checkWithinXYears(convertDateToYYYYMMDD(date), currentDate, 2)) {
          return `Claims history from ${twoYearsBackFromCurrentDate} is available.`;
        }
      }
      return '';
    },

    /**
     * Resets all selected filters.
     */
    resetFilters() {
      // return to the first page
      this.pageNum = 1;

      this.filterResult.enteredID = '';
      this.filterResult.selectedMember = '';
      this.filterResult.selectedClaimType = '';
      this.filterResult.selectedClaimStatus = '';
      this.startDate = '';
      this.endDate = '';

      this.applyFilters();
    },

    // apply filter options
    applyFilters() {
      this.pageNum = 1;
      setQueryString('pageNum', 1); // always move to first page when filter is applied
      // check all filters except date
      this.result = this.allQuotes.filter((quote) => {
        return (
          this.check(quote.Member, this.filterResult.selectedMember) &&
          this.check(quote['Claim type'], this.filterResult.selectedClaimType) &&
          this.check(quote.id.toUpperCase(), this.filterResult.enteredID.toUpperCase()) &&
          this.check(quote.status, this.filterResult.selectedClaimStatus)
        );
      });

      // check date
      this.result = this.result.filter((result) => {
        if (this.startDate === '' && this.endDate === '') {
          return true;
        }
        if (this.endDate === '' && this.startDate !== '') {
          return result.date >= formatFormDate(this.startDate);
        }
        if (this.startDate === '' && this.endDate !== '') {
          return result.date <= formatFormDate(this.endDate);
        }
        return result.date >= formatFormDate(this.startDate) && result.date <= formatFormDate(this.endDate);
      });

      // apply new total number of filtered items
      this.totalNum = this.result.length;
      this.lastPageNumber = Math.ceil(this.totalNum / this.pageSize);

      // Message for no search result
      if (this.allQuotes.length > 0 && this.result.length < 1) {
        this.searchResultMessage = 'Sorry, there is no result found.';
      }
      // first page items
      this.quotes = this.result.slice(0, this.pageSize);
      this.recordsData.data = this.quotes;
    },

    check(keyTobeChecked, filterValue) {
      // if the filter is empty or all
      if (filterValue.startsWith('All ') || filterValue === '') {
        return true;
      }

      return keyTobeChecked === filterValue;
    },

    updateRecordLink() {
      if (this.isClaim) {
        this.recordLink = {
          recordLinkParams: {
            toName: CLAIMS_RESULT,
          },
        };
      } else {
        this.recordLink = {
          recordLinkParams: {
            toName: BENEFIT_QUOTE_RESULT,
          },
        };
      }
    },

    formatAsCurrency,
    getImageUrl,
  },
};
</script>

<style lang="scss" scoped>
.api-loading {
  min-height: 400px;
  margin-bottom: 50px;
}

:deep(#side-content-paragraph) {
  max-width: 680px;
}

:deep(.call-out) {
  h2 {
    text-align: left;
  }
}

.pagination-label {
  min-width: 180px;
  float: right;
  margin-bottom: spacer(3);

  @include mobile {
    position: relative;
  }
}
</style>
